import {
  ApiErrorInstance,
  isApi5xxResponseStatus,
  withHandleError
} from '~/api';
import { HttpStatusCode } from 'axios';
import { getLocalizedString } from '~/i18n/utils.ts';
import { AppBaseNamespaces } from '~/components/AppRouter/utils.ts';
import { miscConstants } from '~/consts.ts';
import * as zod from 'zod';

enum WorkspaceOnboardingSteps {
  WORKSPACE_FORM = 'workspace-form',
  PRODUCT_DETAILS_FORM = 'product-details-form',
  ONBOARDING_LOADING = 'onboarding-loading'
}

enum AuthenticationMode {
  FULL_STACK_AUTH = 'FULL_STACK_AUTH',
  MODULAR_AUTH = 'MODULAR_AUTH'
}

enum ScalekitProducts {
  MCP_AUTH = 'MCP_AUTH',
  AGENT_AUTH = 'AGENT_AUTH',
  MODULAR_SSO = 'MODULAR_SSO',
  MODULAR_SCIM = 'MODULAR_SCIM',
  FULL_STACK_AUTH = 'FULL_STACK_AUTH'
}

const getTranslationKey = (path: string): string => `pages.onboarding.${path}`;
const WORKSPACE_SETUP_STEP_INTERVAL = 2500;
const WORKSPACE_SETUP_STEP_COUNT = 2;

const proceedToWorkspace = (withOnBoardingParam: boolean = true) =>
  window.location.assign(
    `${AppBaseNamespaces.WORKSPACE}${withOnBoardingParam ? `?${miscConstants.QUERY_PARAMS.ONBOARDING_PARAM_NAME}=true` : ''}`
  );

const handleOnboardingError = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  const translationBasePath = 'server_errors.onboarding';
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    return getLocalizedString(`${translationBasePath}.failed`, {
      intl
    });
  }
});

const getWorkspaceOnboardingSchema = (shouldCaptureName: boolean, intl: any) =>
  zod.object({
    ...(shouldCaptureName && {
      firstName: zod
        .string()
        .min(
          1,
          getLocalizedString(
            'commons.onboarding.validation_message_empty_first_name',
            { intl }
          )
        ),
      lastName: zod.string()
    }),
    workspaceDisplayName: zod
      .string()
      .min(
        1,
        getLocalizedString(
          'commons.onboarding.validation_message_empty_workspace_name',
          { intl }
        )
      )
  });

const PRODUCT_TRANSLATION_KEYS: Record<ScalekitProducts, string> = {
  [ScalekitProducts.FULL_STACK_AUTH]: 'scalekit_products.full_stack_auth',
  [ScalekitProducts.MODULAR_SSO]: 'scalekit_products.modular_sso',
  [ScalekitProducts.MODULAR_SCIM]: 'scalekit_products.modular_scim',
  [ScalekitProducts.MCP_AUTH]: 'scalekit_products.mcp_auth',
  [ScalekitProducts.AGENT_AUTH]: 'scalekit_products.agent_auth'
};

const getProductTypeLabel = (product: ScalekitProducts, intl: any): string => {
  const config = {
    [ScalekitProducts.AGENT_AUTH]: getLocalizedString(
      getTranslationKey('agent_auth.header'),
      { intl }
    ),
    [ScalekitProducts.MCP_AUTH]: getLocalizedString(
      getTranslationKey('mcp_auth.header'),
      { intl }
    ),
    [ScalekitProducts.MODULAR_SSO]: getLocalizedString(
      getTranslationKey('modular_sso.header'),
      { intl }
    ),
    [ScalekitProducts.MODULAR_SCIM]: getLocalizedString(
      getTranslationKey('modular_scim.header'),
      { intl }
    ),
    [ScalekitProducts.FULL_STACK_AUTH]: getLocalizedString(
      getTranslationKey('full_stack_auth.header_short'),
      { intl }
    )
  };
  return config[product];
};

export {
  AuthenticationMode,
  WORKSPACE_SETUP_STEP_INTERVAL,
  WORKSPACE_SETUP_STEP_COUNT,
  PRODUCT_TRANSLATION_KEYS,
  getProductTypeLabel,
  WorkspaceOnboardingSteps,
  proceedToWorkspace,
  handleOnboardingError,
  getTranslationKey,
  getWorkspaceOnboardingSchema,
  ScalekitProducts
};
